import { gql } from '@urql/core';

const PROMOTION_FRAGMENT = gql`
  fragment PromotionDetail on Promotion {
    id
    createdAt
    updatedAt
    name
    description
    enabled
    couponCode
    perCustomerUsageLimit
    usageLimit
    startsAt
    endsAt
    translations {
      id
      languageCode
      name
      description
      customFields {
        calloutText
      }
    }
    conditions {
      code
      args {
        name
        value
      }
    }
    actions {
      code
      args {
        name
        value
      }
    }
    customFields {
      isSystem
      promotionType
      bannerText
      bannerColor
      promotionPriority
      offerSlug
      customPageSlug
      showProductLabel
      showStorefrontWidget
      showInCartCouponWidget
    }
  }
`;

export const GET_PROMOTIONS = gql`
  query GetPromotions($options: PromotionListOptions) {
    promotions(options: $options) {
      items {
        ...PromotionDetail
      }
      totalItems
    }
  }
  ${PROMOTION_FRAGMENT}
`;

export const GET_CASHBACK_PROMOTIONS = gql`
  query GetCashbackPromotions($options: PromotionListOptions) {
    cashbackPromotions(options: $options) {
      items {
        ...PromotionDetail
      }
      totalItems
    }
  }
  ${PROMOTION_FRAGMENT}
`;

export const GET_PROMOTION = gql`
  query GetPromotion($id: ID!) {
    promotion(id: $id) {
      ...PromotionDetail
    }
  }
  ${PROMOTION_FRAGMENT}
`;

export const CREATE_PROMOTION = gql`
  mutation CreatePromotion($input: CreatePromotionInput!) {
    createPromotion(input: $input) {
      ... on Promotion {
        ...PromotionDetail
      }
      ... on MissingConditionsError {
        errorCode
        message
      }
    }
  }
  ${PROMOTION_FRAGMENT}
`;

export const UPDATE_PROMOTION = gql`
  mutation UpdatePromotion($input: UpdatePromotionInput!) {
    updatePromotion(input: $input) {
      ... on Promotion {
        ...PromotionDetail
      }
      ... on MissingConditionsError {
        errorCode
        message
      }
    }
  }
  ${PROMOTION_FRAGMENT}
`;

export const DELETE_PROMOTION = gql`
  mutation DeletePromotion($id: ID!) {
    deletePromotion(id: $id) {
      result
      message
    }
  }
`;

export const DELETE_PROMOTIONS = gql`
  mutation DeletePromotions($ids: [ID!]!) {
    deletePromotions(ids: $ids) {
      result
      message
    }
  }
`;

export const GET_PROMOTION_CONDITIONS = gql`
  query GetPromotionConditions {
    promotionConditions {
      code
      description
      args {
        name
        type
        required
        defaultValue
        list
        ui
        label
        description
      }
    }
  }
`;

export const GET_PROMOTION_ACTIONS = gql`
  query GetPromotionActions {
    promotionActions {
      code
      description
      args {
        name
        type
        required
        defaultValue
        list
        ui
        label
        description
      }
    }
  }
`;

export const GET_PROMOTION_COLLECTION_LINK = gql`
  query GetPromotionCollectionLink($promotionId: ID!) {
    promotionCollectionLink(promotionId: $promotionId) {
      id
      slug
      offerSlug
      archived
      collection {
        id
        name
        slug
      }
    }
  }
`;

export const SYNC_PROMOTION_COLLECTION = gql`
  mutation SyncPromotionCollection($input: SyncPromotionCollectionInput!) {
    syncPromotionCollection(input: $input) {
      id
      slug
      offerSlug
      archived
      collection {
        id
        name
        slug
      }
    }
  }
`;

export const ARCHIVE_PROMOTION_COLLECTION = gql`
  mutation ArchivePromotionCollection($promotionId: ID!) {
    archivePromotionCollection(promotionId: $promotionId)
  }
`;

export const CREATE_OFFER_PAGE_FROM_PROMOTION = gql`
  mutation CreateOfferPageFromPromotion($input: CreateOfferPageInput!) {
    createOfferPageFromPromotion(input: $input) {
      id
      name
      slug
      translations {
        languageCode
        name
        slug
      }
    }
  }
`;

export const IS_OFFER_SLUG_AVAILABLE = gql`
  query IsOfferSlugAvailable($slug: String!, $excludePromotionId: ID) {
    isOfferSlugAvailable(slug: $slug, excludePromotionId: $excludePromotionId)
  }
`;

export const GET_CATALOG_DISCOUNTS = gql`
  query GetCatalogDiscounts($options: CatalogDiscountListOptions) {
    catalogDiscounts(options: $options) {
      items {
        id
        createdAt
        updatedAt
        name
        enabled
        startsAt
        endsAt
        priority
        exclusive
        scopeType
        discountType
        discountValue
        minimumPrice
        respectCostFloor
        combinesWithManualSale
        lastRunStats {
          matchedPriceRows
          winningPriceRows
          skippedManualSaleRows
          skippedMissingCostRows
          flooredToMinimumRows
          flooredToCostRows
          lastCalculatedAt
        }
        scopeProductVariantIds
        scopeCollectionIds
        scopeCategoryIds
        scopeFacetValueIds
      }
      totalItems
    }
  }
`;

export const GET_CATALOG_DISCOUNT = gql`
  query GetCatalogDiscount($id: ID!) {
    catalogDiscount(id: $id) {
      id
      createdAt
      updatedAt
      name
      enabled
      startsAt
      endsAt
      priority
      exclusive
      scopeType
      discountType
      discountValue
      minimumPrice
      respectCostFloor
      combinesWithManualSale
      lastRunStats {
        matchedPriceRows
        winningPriceRows
        skippedManualSaleRows
        skippedMissingCostRows
        flooredToMinimumRows
        flooredToCostRows
        lastCalculatedAt
      }
      scopeProductVariantIds
      scopeCollectionIds
      scopeCategoryIds
      scopeFacetValueIds
      salesChannelCodes
    }
  }
`;

export const CREATE_CATALOG_DISCOUNT = gql`
  mutation CreateCatalogDiscount($input: CreateCatalogDiscountInput!) {
    createCatalogDiscount(input: $input) {
      id
      name
      enabled
      scopeType
      discountType
      discountValue
      minimumPrice
      respectCostFloor
      combinesWithManualSale
      salesChannelCodes
    }
  }
`;

export const UPDATE_CATALOG_DISCOUNT = gql`
  mutation UpdateCatalogDiscount($input: UpdateCatalogDiscountInput!) {
    updateCatalogDiscount(input: $input) {
      id
      name
      enabled
      scopeType
      discountType
      discountValue
      minimumPrice
      respectCostFloor
      combinesWithManualSale
      salesChannelCodes
    }
  }
`;

export const DELETE_CATALOG_DISCOUNT = gql`
  mutation DeleteCatalogDiscount($id: ID!) {
    deleteCatalogDiscount(id: $id) {
      result
      message
    }
  }
`;

export const GET_LOYALTY_SETTINGS = gql`
  query GetLoyaltySettings {
    loyaltySettings {
      id
      channelId
      enabled
      pointsExpiryDays
    }
  }
`;

export const UPDATE_LOYALTY_SETTINGS = gql`
  mutation UpdateLoyaltySettings($input: UpdateLoyaltySettingsInput!) {
    updateLoyaltySettings(input: $input) {
      id
      channelId
      enabled
      pointsExpiryDays
    }
  }
`;

export const GET_LOYALTY_EARN_RULES = gql`
  query GetLoyaltyEarnRules {
    loyaltyEarnRules {
      id
      channelId
      type
      enabled
      pointsValue
      config
      createdAt
      updatedAt
    }
  }
`;

export const GET_LOYALTY_EARN_RULE_TYPES = gql`
  query GetLoyaltyEarnRuleTypes {
    loyaltyEarnRuleTypes
  }
`;

export const CREATE_LOYALTY_EARN_RULE = gql`
  mutation CreateLoyaltyEarnRule($input: CreateLoyaltyEarnRuleInput!) {
    createLoyaltyEarnRule(input: $input) {
      id
      channelId
      type
      enabled
      pointsValue
      config
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_LOYALTY_EARN_RULE = gql`
  mutation UpdateLoyaltyEarnRule($id: ID!, $input: UpdateLoyaltyEarnRuleInput!) {
    updateLoyaltyEarnRule(id: $id, input: $input) {
      id
      channelId
      type
      enabled
      pointsValue
      config
      createdAt
      updatedAt
    }
  }
`;

export const GET_LOYALTY_REWARDS = gql`
  query GetLoyaltyRewards {
    loyaltyRewards {
      id
      promotionId
      name
      enabled
      actionCode
      args
    }
  }
`;

export const UPDATE_LOYALTY_REWARD = gql`
  mutation UpdateLoyaltyReward($id: ID!, $input: UpdateLoyaltyRewardInput!) {
    updateLoyaltyReward(id: $id, input: $input) {
      id
      promotionId
      name
      enabled
      actionCode
      args
    }
  }
`;

export const GET_CUSTOMER_WALLET_BALANCE = gql`
  query GetCustomerWalletBalance($customerId: ID!) {
    customerWalletBalance(customerId: $customerId) {
      pointsBalance
      walletBalance
      lifetimePoints
    }
  }
`;

export const GET_CUSTOMER_WALLET_TRANSACTIONS = gql`
  query GetCustomerWalletTransactions($customerId: ID!, $options: WalletTransactionListOptions) {
    customerWalletTransactions(customerId: $customerId, options: $options) {
      items {
        id
        createdAt
        type
        amount
        currency
        balance
        description
        referenceType
        referenceId
        adminId
      }
      totalItems
    }
  }
`;

export const ADJUST_CUSTOMER_WALLET = gql`
  mutation AdjustCustomerWallet($input: AdjustCustomerWalletInput!) {
    adjustCustomerWallet(input: $input) {
      success
      errorCode
      transaction {
        id
        type
        amount
        currency
        balance
        description
      }
    }
  }
`;

export const GET_ABANDONED_CART_SETTINGS = gql`
  query GetAbandonedCartSettings {
    abandonedCartSettings {
      enabled
      abandonmentMinutes
      steps {
        delayMinutes
        channel
        discountPercent
        templateVariant
      }
      automationId
    }
  }
`;

export const UPDATE_ABANDONED_CART_SETTINGS = gql`
  mutation UpdateAbandonedCartSettings($input: UpdateAbandonedCartSettingsInput!) {
    updateAbandonedCartSettings(input: $input) {
      enabled
      abandonmentMinutes
      steps {
        delayMinutes
        channel
        discountPercent
        templateVariant
      }
      automationId
    }
  }
`;

export const GET_ABANDONED_CART_ANALYTICS = gql`
  query GetAbandonedCartAnalytics($dateRange: DateRangeInput) {
    abandonedCartAnalytics(dateRange: $dateRange) {
      totalAbandoned
      totalRecovered
      totalClicked
      clickThroughRate
      recoveryRate
      revenueRecovered
      byStep {
        stepIndex
        sent
        recovered
        clicked
      }
    }
  }
`;

const ABANDONED_CART_ITEM_FRAGMENT = gql`
  fragment AbandonedCartItem on Order {
    id
    code
    state
    totalWithTax
    currencyCode
    createdAt
    updatedAt
    orderPlacedAt
    customFields {
      abandonedAt
      recoverySource
    }
    customer {
      id
      firstName
      lastName
      emailAddress
      phoneNumber
    }
    shippingAddress {
      streetLine1
      city
      phoneNumber
    }
    lines {
      id
      quantity
      productVariant {
        name
        sku
        featuredAsset {
          preview
        }
      }
    }
    shippingLines {
      shippingMethod {
        name
      }
    }
  }
`;

export const GET_ABANDONED_CARTS = gql`
  query GetAbandonedCarts($options: OrderListOptions) {
    orders(options: $options) {
      items {
        ...AbandonedCartItem
      }
      totalItems
    }
  }
  ${ABANDONED_CART_ITEM_FRAGMENT}
`;

export const SEND_ABANDONED_CART_REMINDER = gql`
  mutation SendAbandonedCartReminder(
    $orderId: ID!
    $channel: String!
    $discountPercent: Int
    $templateVariant: String
  ) {
    sendAbandonedCartReminder(
      orderId: $orderId
      channel: $channel
      discountPercent: $discountPercent
      templateVariant: $templateVariant
    )
  }
`;

export const GET_AFFILIATES = gql`
  query GetAffiliates($options: AffiliateListOptions) {
    affiliates(options: $options) {
      items {
        id
        createdAt
        name
        email
        phone
        status
        commissionRate
        discountRate
        couponCode
        customerId
        totalOrders
        totalRevenue
        totalCommission
      }
      totalItems
    }
  }
`;

export const GET_AFFILIATE = gql`
  query GetAffiliate($id: ID!) {
    affiliate(id: $id) {
      id
      createdAt
      updatedAt
      name
      email
      phone
      status
      commissionRate
      discountRate
      couponCode
      promotionId
      customerId
      totalOrders
      totalRevenue
      totalCommission
      notes
    }
  }
`;

export const CREATE_AFFILIATE = gql`
  mutation CreateAffiliate($input: CreateAffiliateInput!) {
    createAffiliate(input: $input) {
      success
      errorCode
      message
      affiliate {
        id
        name
        couponCode
        status
      }
    }
  }
`;

export const UPDATE_AFFILIATE = gql`
  mutation UpdateAffiliate($id: ID!, $input: UpdateAffiliateInput!) {
    updateAffiliate(id: $id, input: $input) {
      success
      errorCode
      message
      affiliate {
        id
        name
        email
        commissionRate
        discountRate
      }
    }
  }
`;

export const APPROVE_AFFILIATE = gql`
  mutation ApproveAffiliate($id: ID!) {
    approveAffiliate(id: $id) {
      success
      errorCode
      message
      affiliate {
        id
        status
        promotionId
      }
    }
  }
`;

export const SUSPEND_AFFILIATE = gql`
  mutation SuspendAffiliate($id: ID!) {
    suspendAffiliate(id: $id) {
      success
      errorCode
      message
      affiliate {
        id
        status
      }
    }
  }
`;

export const REJECT_AFFILIATE = gql`
  mutation RejectAffiliate($id: ID!) {
    rejectAffiliate(id: $id) {
      success
      errorCode
      message
      affiliate {
        id
        status
      }
    }
  }
`;

export const GET_AFFILIATE_SETTINGS = gql`
  query GetAffiliateSettings {
    affiliateSettings {
      id
      channelId
      isEnabled
      defaultCommissionRate
      defaultDiscountRate
      commissionBasis
      autoApprove
      minimumPayoutAmount
      termsAndConditions
    }
  }
`;

export const UPDATE_AFFILIATE_SETTINGS = gql`
  mutation UpdateAffiliateSettings($input: UpdateAffiliateSettingsInput!) {
    updateAffiliateSettings(input: $input) {
      id
      isEnabled
      defaultCommissionRate
      defaultDiscountRate
      commissionBasis
      autoApprove
      minimumPayoutAmount
      termsAndConditions
    }
  }
`;

export const GET_REFERRAL_SETTINGS = gql`
  query GetReferralSettings {
    referralSettings {
      id
      channelId
      enabled
      referrerRewardType
      referrerRewardValue
      referredDiscountPercent
      minOrderForReward
      minOrderForDiscount
      maxReferralsPerCustomer
      cookieDurationDays
      shareMessages
    }
  }
`;

export const UPDATE_REFERRAL_SETTINGS = gql`
  mutation UpdateReferralSettings($input: UpdateReferralSettingsInput!) {
    updateReferralSettings(input: $input) {
      id
      channelId
      enabled
      referrerRewardType
      referrerRewardValue
      referredDiscountPercent
      minOrderForReward
      minOrderForDiscount
      maxReferralsPerCustomer
      cookieDurationDays
      shareMessages
    }
  }
`;

export const GET_REFERRAL_STATS = gql`
  query GetReferralStats {
    referralStats {
      totalReferrals
      conversions
      conversionRate
    }
  }
`;

export const GET_REFERRALS = gql`
  query GetReferrals($options: ReferralListOptions) {
    referrals(options: $options) {
      items {
        id
        createdAt
        referrerCustomerId
        referredCustomerId
        referredOrderId
        referralCode
        status
        rewardAmount
        rewardCurrency
        rewardReversedAmount
        rewardWrittenOffAmount
      }
      totalItems
    }
  }
`;

export const GET_ANNOUNCEMENT_BARS = gql`
  query GetAnnouncementBars($options: AnnouncementBarListOptions) {
    announcementBars(options: $options) {
      items {
        id
        createdAt
        updatedAt
        name
        text
        enabled
        priority
        link
        bgColor
        textColor
        dismissible
        startAt
        endAt
        promotionId
        couponCode
        displayMode
        translations {
          id
          languageCode
          text
        }
      }
      totalItems
    }
  }
`;

export const GET_ANNOUNCEMENT_BAR = gql`
  query GetAnnouncementBar($id: ID!) {
    announcementBar(id: $id) {
      id
      createdAt
      updatedAt
      name
      text
      enabled
      priority
      link
      bgColor
      textColor
      dismissible
      startAt
      endAt
      promotionId
      couponCode
      displayMode
      translations {
        id
        languageCode
        text
      }
    }
  }
`;

export const CREATE_ANNOUNCEMENT_BAR = gql`
  mutation CreateAnnouncementBar($input: CreateAnnouncementBarInput!) {
    createAnnouncementBar(input: $input) {
      id
      name
      text
      enabled
      priority
      bgColor
      textColor
      translations {
        id
        languageCode
        text
      }
    }
  }
`;

export const UPDATE_ANNOUNCEMENT_BAR = gql`
  mutation UpdateAnnouncementBar($input: UpdateAnnouncementBarInput!) {
    updateAnnouncementBar(input: $input) {
      id
      name
      text
      enabled
      priority
      bgColor
      textColor
      translations {
        id
        languageCode
        text
      }
    }
  }
`;

export const DELETE_ANNOUNCEMENT_BAR = gql`
  mutation DeleteAnnouncementBar($id: ID!) {
    deleteAnnouncementBar(id: $id) {
      result
      message
    }
  }
`;

export const GET_METHOD_RESTRICTIONS = gql`
  query GetMethodRestrictions($targetType: String, $matchType: String, $source: String) {
    methodRestrictions(targetType: $targetType, matchType: $matchType, source: $source) {
      id
      createdAt
      matchType
      matchValue
      targetType
      targetId
      restrictionMode
      source
      message
      translations {
        languageCode
        message
      }
    }
  }
`;

export const CREATE_METHOD_RESTRICTION = gql`
  mutation CreateMethodRestriction($input: CreateMethodRestrictionInput!) {
    createMethodRestriction(input: $input) {
      id
      matchType
      matchValue
      targetType
      targetId
      source
    }
  }
`;

export const UPDATE_METHOD_RESTRICTION = gql`
  mutation UpdateMethodRestriction($id: ID!, $input: UpdateMethodRestrictionInput!) {
    updateMethodRestriction(id: $id, input: $input) {
      id
    }
  }
`;

export const DELETE_METHOD_RESTRICTION = gql`
  mutation DeleteMethodRestriction($id: ID!) {
    deleteMethodRestriction(id: $id)
  }
`;

export const GET_TAGS = gql`
  query GetTags($options: TagListOptions) {
    tags(options: $options) {
      items {
        id
        value
      }
      totalItems
    }
  }
`;

export const GET_PROMOTION_CAMPAIGN = gql`
  query GetPromotionCampaign($promotionId: ID!) {
    promotionCampaign(promotionId: $promotionId) {
      id name active startsAt endsAt usageLimit perCustomerUsageLimit codeUsageLimit usageCount
      codes { code promotionId usageCount }
    }
  }
`;
export const ADOPT_PROMOTION_CAMPAIGN = gql`
  mutation AdoptPromotionCampaign($promotionId: ID!, $codeUsageLimit: Int) {
    adoptPromotionCampaign(promotionId: $promotionId, codeUsageLimit: $codeUsageLimit) { id }
  }
`;
export const ADD_PROMOTION_CAMPAIGN_CODES = gql`
  mutation AddPromotionCampaignCodes($input: CampaignCodeBatchInput!) {
    addPromotionCampaignCodes(input: $input) { codes replayed }
  }
`;
export const SET_PROMOTION_CAMPAIGN_ACTIVE = gql`
  mutation SetPromotionCampaignActive($campaignId: ID!, $active: Boolean!) {
    setPromotionCampaignActive(campaignId: $campaignId, active: $active) { id active }
  }
`;

// Task 44: read-only calendar of promotion and catalog-sale schedules and their time overlaps.
// Deliberately has no winner/priority fields (owner decision 1).
export const GET_PROMOTION_CALENDAR = gql`
  query GetPromotionCalendar($input: PromotionCalendarInput!) {
    promotionCalendar(input: $input) {
      from
      to
      catalogSalesIncluded
      truncated
      entries {
        key
        kind
        id
        name
        enabled
        startsAt
        endsAt
        couponCode
      }
      overlaps {
        entryKeys
        kinds
        startsAt
        endsAt
      }
    }
  }
`;
